import { createElement, forwardRef, useMemo } from 'react';

import {
  findContextReferenceSpans,
  type ContextLinkTarget,
} from '@/lib/chat/context-links';

type ChatComposerMentionMirrorProps = {
  text: string;
  linkTargets: ContextLinkTarget[];
};

export const ChatComposerMentionMirror = forwardRef<
  HTMLDivElement,
  ChatComposerMentionMirrorProps
>(function ChatComposerMentionMirror({ text, linkTargets }, ref) {
  const segments = useMemo(() => {
    const spans = findContextReferenceSpans(text, linkTargets);
    if (spans.length === 0) {
      return [{ kind: 'text' as const, text }];
    }

    const parts: Array<{ kind: 'text' | 'mention'; text: string }> = [];
    let cursor = 0;
    for (const span of spans) {
      if (span.start > cursor) {
        parts.push({ kind: 'text', text: text.slice(cursor, span.start) });
      }
      parts.push({ kind: 'mention', text: text.slice(span.start, span.end) });
      cursor = span.end;
    }
    if (cursor < text.length) {
      parts.push({ kind: 'text', text: text.slice(cursor) });
    }
    return parts;
  }, [text, linkTargets]);

  return createElement(
    'div',
    { ref, className: 'chat-composer-mirror', 'aria-hidden': true },
    segments.map((segment, index) =>
      segment.kind === 'mention'
        ? createElement(
            'span',
            { key: index, className: 'chat-composer-mention cs-context-link' },
            segment.text,
          )
        : createElement('span', { key: index }, segment.text),
    ),
    text.endsWith('\n') ? createElement('br') : null,
  );
});

export function syncComposerMirrorScroll(
  textarea: HTMLTextAreaElement | null,
  mirror: HTMLDivElement | null,
): void {
  if (!textarea || !mirror) {
    return;
  }
  mirror.style.transform = `translate(${-textarea.scrollLeft}px, ${-textarea.scrollTop}px)`;
}
